// /////////////////////////////////////////////////////////////
// [ファイル] EventAssignmentPanel.tsx
// [概要] イベント詳細の「配置」タブ。参加者をグループ (卓 / 組 / レーン) と
//        その中の順番 (席 / 打順 / 投順) で並べて表示・編集・印刷する。
//        呼び名は mst_event の group_unit_label / position_unit_label を使うため、
//        忘年会の席次表・ゴルフの組合表・ボウリングの投順表を同じ画面で扱える。
//        - 表示: グループごとのカード (補足・順番付き氏名・会社)。未配置は末尾にまとめる
//        - 編集: 参加者をドラッグ＆ドロップで別グループ・グループ内の位置・未配置・新しいグループへ移す
//          (dnd-kit)。移動先と移動元の順番は 1 から振り直す。番号入力も残しているが、入力途中で
//          グループ分けし直すとフォーカスを失う (「21」の「2」で 2 組へ飛ぶ) ため、確定時のみ反映する。
//          保存で全行を一括送信する (グループ番号と順番がともに空なら配置解除)
//        - 当社社員: どのイベントでも各グループへ社員を配置する運用のため、社員一覧から
//          参加者へ追加でき (社員用の申込を自動で持つ)、社員バッジと「社員なし / 社員 2 名以上」の
//          警告をグループ見出しに出す。社員行は配置ごと外せる (社外参加者は申込の記録なので不可)
//        - 印刷: 別ウィンドウに一覧 (グループ × 順番) を描画して印刷ダイアログを開く。社員は ★ 印
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_FAB_CTA_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import LoadingView from './LoadingView';
import type { EventMaster, EventParticipantRow } from '../hooks/useEventManagement';

export interface AssignmentInput {
  participant_id: number;
  table_no: string | null;
  seat_no: string | null;
  group_note: string | null;
}

interface Props {
  event: EventMaster;
  participants: EventParticipantRow[];
  loading: boolean;
  canEdit: boolean;
  onSave: (rows: AssignmentInput[]) => Promise<{ ok: boolean; error?: string }>;
  onAddStaff?: (staffUserIds: string[]) => Promise<{ ok: boolean; error?: string; added?: number; skipped?: number }>;
  onRemoveStaff?: (participantId: number) => Promise<{ ok: boolean; error?: string }>;
}

type Draft = { table_no: string; seat_no: string; group_note: string };
type DropTarget = { kind: 'before'; participantId: number } | { kind: 'end'; key: string } | { kind: 'unassigned' } | { kind: 'new' };

const EMPTY_DRAFT: Draft = { table_no: '', seat_no: '', group_note: '' };
const fullName = (p: EventParticipantRow) => `${p.last_name || ''} ${p.first_name || ''}`.trim();

// "13組" "A" "3" のような値を自然順 (数字は数値、文字は辞書順) で並べる
const naturalKey = (v: string | null | undefined): [number, string] => {
  const s = (v ?? '').trim();
  const m = s.match(/^(\d+)/);
  return [m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER, s];
};
const naturalCompare = (a: string | null | undefined, b: string | null | undefined) => {
  const [an, as] = naturalKey(a);
  const [bn, bs] = naturalKey(b);
  return an !== bn ? an - bn : as.localeCompare(bs, 'ja');
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// 役職・会社名に共通の補足テキスト書式 (1 行に収め、はみ出しは … で省略)
const SUB_TEXT_SX = { fontSize: '11px', color: '#64748b', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const STAFF_CHIP_SX = { height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' };
const INPUT_SX = { width: 56, '& .MuiInputBase-input': { fontSize: '12px', py: 0.5, px: 0.5, textAlign: 'center' } };

// 番号入力。入力途中で親のグループ分けを動かさないよう、確定 (blur / Enter) 時だけ onCommit する
const CommitInput = React.memo(function CommitInput({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder: string;
  onCommit: (v: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    if (text !== value) onCommit(text);
  };
  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      sx={INPUT_SX}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
});

// 参加者 1 行 (編集中はドラッグ可能、かつ「この人の前に挿入」のドロップ先)
const MemberRow: React.FC<{
  p: EventParticipantRow;
  draft: Draft;
  editing: boolean;
  groupLabel: string;
  positionLabel: string;
  canRemoveStaff: boolean;
  onCommit: (id: number, patch: Partial<Draft>) => void;
  onRemoveStaff: (p: EventParticipantRow) => void;
}> = ({ p, draft, editing, groupLabel, positionLabel, canRemoveStaff, onCommit, onRemoveStaff }) => {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: `m-${p.id}`, disabled: !editing });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `before-${p.id}`, disabled: !editing });
  return (
    <Box
      ref={setDropRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.4,
        borderBottom: '1px dashed #e2e8f0',
        '&:last-of-type': { borderBottom: 'none' },
        borderTop: isOver ? '2px solid #3b82f6' : '2px solid transparent',
        opacity: isDragging ? 0.35 : 1,
      }}
    >
      {editing && (
        <Box ref={setDragRef} {...attributes} {...listeners} sx={{ display: 'flex', cursor: 'grab', color: '#94a3b8', touchAction: 'none' }} title="ドラッグして移動">
          <Icons.DragIndicator sx={{ fontSize: 18 }} />
        </Box>
      )}
      {editing ? (
        <>
          <CommitInput value={draft.table_no} placeholder={groupLabel} onCommit={(v) => onCommit(p.id, { table_no: v })} />
          <CommitInput value={draft.seat_no} placeholder={positionLabel} onCommit={(v) => onCommit(p.id, { seat_no: v })} />
        </>
      ) : (
        <Typography sx={{ fontSize: '11px', color: '#64748b', minWidth: 44, whiteSpace: 'nowrap' }}>
          {p.seat_no ? `${positionLabel}${p.seat_no}` : ''}
        </Typography>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* 1 行目 = 氏名 + 役職 (会社名と同じ書式、社外のみ) + 社員バッジ。氏名とバッジは縮めず、長い役職だけ省略する */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', flexShrink: 0 }}>{fullName(p) || '(氏名未設定)'}</Typography>
          {p.position_display && !p.is_staff && (
            <Typography sx={{ ...SUB_TEXT_SX, minWidth: 0 }} title={p.position_display}>
              {p.position_display}
            </Typography>
          )}
          {p.is_staff && <Chip label="社員" size="small" sx={{ ...STAFF_CHIP_SX, flexShrink: 0 }} />}
        </Box>
        <Typography sx={SUB_TEXT_SX}>{p.company_name}</Typography>
      </Box>
      {p.is_staff && canRemoveStaff && !editing && (
        <Tooltip title="参加者から外す">
          <IconButton size="small" onClick={() => onRemoveStaff(p)}>
            <Icons.PersonRemove sx={{ fontSize: 16, color: '#94a3b8' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// ドロップ先の帯 (グループ末尾 / 未配置 / 新しいグループ)
const DropStrip: React.FC<{ id: string; label: string; disabled: boolean; accent?: string }> = ({ id, label, disabled, accent = '#3b82f6' }) => {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  if (disabled) return null;
  return (
    <Box
      ref={setNodeRef}
      sx={{
        mt: 0.5,
        py: 0.5,
        textAlign: 'center',
        fontSize: '11px',
        color: isOver ? '#fff' : '#94a3b8',
        bgcolor: isOver ? accent : 'transparent',
        border: '1px dashed',
        borderColor: isOver ? accent : '#cbd5e1',
        borderRadius: 1,
        transition: 'background-color 120ms',
      }}
    >
      {label}
    </Box>
  );
};

const EventAssignmentPanel: React.FC<Props> = ({ event, participants, loading, canEdit, onSave, onAddStaff, onRemoveStaff }) => {
  const notify = useNotification();
  const confirm = useConfirm();
  const { staffList } = useMasterData();
  const groupLabel = event.group_unit_label || '卓';
  const positionLabel = event.position_unit_label || '席';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [activeId, setActiveId] = useState<number | null>(null);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [staffPick, setStaffPick] = useState<any[]>([]);
  const [staffSaving, setStaffSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const byId = useMemo(() => new Map(participants.map((p) => [p.id, p])), [participants]);

  // 編集開始時 (と参加者の再取得時) に現在値から下書きを作る
  useEffect(() => {
    if (!editing) return;
    const next: Record<number, Draft> = {};
    participants.forEach((p) => {
      next[p.id] = { table_no: p.table_no ?? '', seat_no: p.seat_no ?? '', group_note: p.group_note ?? '' };
    });
    setDrafts(next);
  }, [editing, participants]);

  const draftOf = useCallback(
    (p: EventParticipantRow): Draft =>
      editing && drafts[p.id] ? drafts[p.id] : { table_no: p.table_no ?? '', seat_no: p.seat_no ?? '', group_note: p.group_note ?? '' },
    [editing, drafts],
  );

  // 表示用のグループ分け (編集中は確定済みの下書きで再構成する)
  const groups = useMemo(() => {
    const map = new Map<string, { note: string; members: EventParticipantRow[] }>();
    const unassigned: EventParticipantRow[] = [];
    participants.forEach((p) => {
      const d = draftOf(p);
      const key = d.table_no.trim();
      if (!key) {
        unassigned.push(p);
        return;
      }
      const g = map.get(key) ?? { note: '', members: [] };
      if (!g.note && d.group_note.trim()) g.note = d.group_note.trim();
      g.members.push(p);
      map.set(key, g);
    });
    const list = Array.from(map.entries())
      .sort(([a], [b]) => naturalCompare(a, b))
      .map(([key, g]) => ({
        key,
        note: g.note,
        members: g.members.slice().sort((a, b) => naturalCompare(draftOf(a).seat_no, draftOf(b).seat_no)),
        staffCount: g.members.filter((m) => m.is_staff).length,
      }));
    unassigned.sort((a, b) => (a.reception_no + a.branch_no).localeCompare(b.reception_no + b.branch_no));
    return { list, unassigned };
  }, [participants, draftOf]);

  const assignedCount = participants.length - groups.unassigned.length;
  const staffTotal = participants.filter((p) => p.is_staff).length;
  const groupsWithoutStaff = groups.list.filter((g) => g.staffCount === 0).length;
  const groupsWithManyStaff = groups.list.filter((g) => g.staffCount >= 2).length;

  // 番号入力の確定 (グループを変えたときは移動先の末尾に付け、両グループの順番を振り直す)
  const commitDraft = useCallback(
    (id: number, patch: Partial<Draft>) => {
      setDrafts((prev) => {
        const cur = prev[id] ?? EMPTY_DRAFT;
        if (patch.table_no !== undefined && patch.table_no.trim() !== cur.table_no.trim()) {
          const key = patch.table_no.trim();
          return moveInDrafts(prev, id, key ? { kind: 'end', key } : { kind: 'unassigned' });
        }
        return { ...prev, [id]: { ...cur, ...patch } };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [participants],
  );

  // グループの補足はそのグループの全員に同じ値を入れる (行単位の列に持つため)
  const setGroupNote = (groupKey: string, note: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const id = Number(k);
        if ((next[id].table_no ?? '').trim() === groupKey) next[id] = { ...next[id], group_note: note };
      });
      return next;
    });

  // 下書き上で参加者を移動し、移動先・移動元グループの順番を 1 から振り直す
  const moveInDrafts = (prev: Record<number, Draft>, pid: number, target: DropTarget): Record<number, Draft> => {
    const next: Record<number, Draft> = { ...prev };
    const cur = next[pid] ?? EMPTY_DRAFT;
    const srcKey = cur.table_no.trim();
    const membersOf = (key: string, exclude: number) =>
      participants
        .filter((p) => p.id !== exclude && (next[p.id]?.table_no ?? '').trim() === key)
        .sort((a, b) => naturalCompare(next[a.id]?.seat_no, next[b.id]?.seat_no));
    const renumber = (key: string, ordered: EventParticipantRow[]) => {
      const note = ordered.map((p) => next[p.id]?.group_note ?? '').find((n) => n.trim()) ?? '';
      ordered.forEach((p, i) => {
        next[p.id] = { table_no: key, seat_no: String(i + 1), group_note: note };
      });
    };
    let dstKey: string | null = null;
    if (target.kind === 'unassigned') {
      next[pid] = { ...EMPTY_DRAFT };
    } else {
      let insertBefore: number | null = null;
      if (target.kind === 'before') {
        const t = byId.get(target.participantId);
        dstKey = t ? (next[t.id]?.table_no ?? '').trim() : '';
        insertBefore = target.participantId;
        if (!dstKey) {
          // 未配置の人の前へ落とした場合は未配置扱い
          next[pid] = { ...EMPTY_DRAFT };
          dstKey = null;
        }
      } else if (target.kind === 'end') {
        dstKey = target.key;
      } else {
        const nums = Object.values(next)
          .map((d) => parseInt((d.table_no ?? '').trim(), 10))
          .filter((n) => Number.isFinite(n));
        dstKey = String((nums.length ? Math.max(...nums) : 0) + 1);
      }
      if (dstKey) {
        const me = byId.get(pid)!;
        const ordered = membersOf(dstKey, pid);
        const idx = insertBefore != null ? ordered.findIndex((p) => p.id === insertBefore) : -1;
        if (idx >= 0) ordered.splice(idx, 0, me);
        else ordered.push(me);
        renumber(dstKey, ordered);
      }
    }
    if (srcKey && srcKey !== dstKey) {
      renumber(srcKey, membersOf(srcKey, pid));
    }
    return next;
  };

  const handleDragStart = (e: DragStartEvent) => {
    const id = Number(String(e.active.id).replace('m-', ''));
    setActiveId(id);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const pid = Number(String(e.active.id).replace('m-', ''));
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || !byId.has(pid)) return;
    let target: DropTarget | null = null;
    if (overId.startsWith('before-')) {
      const t = Number(overId.replace('before-', ''));
      if (t === pid) return;
      target = { kind: 'before', participantId: t };
    } else if (overId.startsWith('end-')) {
      target = { kind: 'end', key: overId.replace('end-', '') };
    } else if (overId === 'unassigned') {
      target = { kind: 'unassigned' };
    } else if (overId === 'new') {
      target = { kind: 'new' };
    }
    if (target) setDrafts((prev) => moveInDrafts(prev, pid, target!));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const rows: AssignmentInput[] = participants.map((p) => {
        const d = drafts[p.id] ?? EMPTY_DRAFT;
        return {
          participant_id: p.id,
          table_no: d.table_no.trim() || null,
          seat_no: d.seat_no.trim() || null,
          group_note: d.table_no.trim() ? d.group_note.trim() || null : null,
        };
      });
      const r = await onSave(rows);
      if (r.ok) {
        notify.success('配置を保存しました');
        setEditing(false);
      } else {
        notify.error(r.error || '配置の保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  // 社員一覧 (既に参加者にいる社員は除く)
  const staffOptions = useMemo(() => {
    const already = new Set(participants.map((p) => p.staff_user_id).filter(Boolean));
    return (staffList || []).filter((s: any) => s.staff_id && !already.has(s.staff_id));
  }, [staffList, participants]);

  const handleAddStaff = async () => {
    if (!onAddStaff || staffPick.length === 0) return;
    setStaffSaving(true);
    try {
      const r = await onAddStaff(staffPick.map((s) => s.staff_id));
      if (r.ok) {
        notify.success(`社員 ${r.added ?? staffPick.length} 名を参加者に追加しました${r.skipped ? `（${r.skipped} 名は追加済み）` : ''}`);
        setStaffDialogOpen(false);
        setStaffPick([]);
      } else {
        notify.error(r.error || '社員の追加に失敗しました');
      }
    } finally {
      setStaffSaving(false);
    }
  };

  const handleRemoveStaff = async (p: EventParticipantRow) => {
    if (!onRemoveStaff) return;
    const ok = await confirm({
      title: '社員を参加者から外す',
      message: `「${fullName(p)}」をこのイベントの参加者から外します。配置も解除されます。よろしいですか？`,
      destructive: true,
      confirmText: '外す',
      cancelText: 'キャンセル',
    });
    if (!ok) return;
    const r = await onRemoveStaff(p.id);
    if (r.ok) notify.success('参加者から外しました');
    else notify.error(r.error || '社員の除外に失敗しました');
  };

  const handlePrint = () => {
    const title = `${event.event_name} ${groupLabel}割`;
    const cards = groups.list
      .map(
        (g) => `
        <table class="g">
          <thead><tr><th colspan="3">${escapeHtml(g.key)}${escapeHtml(groupLabel)}${g.note ? `<span class="note">${escapeHtml(g.note)}</span>` : ''}</th></tr></thead>
          <tbody>${g.members
            .map((m) => {
              const d = draftOf(m);
              return `<tr><td class="pos">${escapeHtml(positionLabel)}${escapeHtml(d.seat_no || '')}</td><td class="name">${m.is_staff ? '★' : ''}${escapeHtml(fullName(m))}</td><td class="co">${escapeHtml([m.is_staff ? null : m.position_display, m.company_name].filter(Boolean).join('　'))}</td></tr>`;
            })
            .join('')}</tbody>
        </table>`,
      )
      .join('');
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  body{font-family:"Yu Gothic","Meiryo",sans-serif;margin:16mm;color:#111}
  h1{font-size:18px;margin:0 0 4px} .sub{font-size:12px;color:#555;margin-bottom:12px}
  .wrap{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
  table.g{border-collapse:collapse;width:100%;break-inside:avoid;font-size:12px}
  table.g th{background:#f1f5f9;border:1px solid #cbd5e1;padding:4px 6px;text-align:left;font-size:13px}
  table.g td{border:1px solid #cbd5e1;padding:3px 6px}
  td.pos{width:3.5em;color:#555;white-space:nowrap} td.name{font-weight:700;white-space:nowrap} td.co{color:#555;font-size:11px}
  .note{font-weight:400;color:#555;margin-left:8px;font-size:11px}
  @media print{body{margin:10mm}}
</style></head><body>
<h1>${escapeHtml(title)}</h1>
<div class="sub">開催日 ${escapeHtml(event.event_date || '未定')}${event.venue ? `　会場 ${escapeHtml(event.venue)}` : ''}　${groups.list.length}${escapeHtml(groupLabel)} / ${assignedCount}名　（★ 当社社員）</div>
<div class="wrap">${cards}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) {
      notify.warning('印刷ウィンドウを開けませんでした。ポップアップの許可を確認してください');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <LoadingView message="配置を取得中" height="40vh" size={48} />;

  const renderMember = (p: EventParticipantRow) => (
    <MemberRow
      key={p.id}
      p={p}
      draft={draftOf(p)}
      editing={editing}
      groupLabel={groupLabel}
      positionLabel={positionLabel}
      canRemoveStaff={canEdit && !!onRemoveStaff}
      onCommit={commitDraft}
      onRemoveStaff={handleRemoveStaff}
    />
  );
  const active = activeId != null ? byId.get(activeId) : undefined;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* 操作行 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`${groups.list.length}${groupLabel} / 配置済 ${assignedCount}名 / 未配置 ${groups.unassigned.length}名 / 社員 ${staffTotal}名`} sx={{ fontSize: '11px', height: 24, fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
        {groupsWithoutStaff > 0 && (
          <Chip size="small" icon={<Icons.WarningAmber sx={{ fontSize: 14 }} />} label={`社員のいない${groupLabel} ${groupsWithoutStaff}`} sx={{ fontSize: '11px', height: 24, fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
        )}
        {groupsWithManyStaff > 0 && (
          <Chip size="small" icon={<Icons.WarningAmber sx={{ fontSize: 14 }} />} label={`社員が2名以上の${groupLabel} ${groupsWithManyStaff}`} sx={{ fontSize: '11px', height: 24, fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
        )}
        <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
          {editing
            ? `氏名をドラッグして${groupLabel}や順番を入れ替えられます。番号入力は確定時に反映されます`
            : `呼び名 (${groupLabel} / ${positionLabel}) はイベント一覧の編集で変更できます`}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
              <Button onClick={handleSave} disabled={saving} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>保存する</Button>
            </>
          ) : (
            <>
              <Button onClick={handlePrint} disabled={groups.list.length === 0} startIcon={<Icons.Print />} sx={STANDARD_TEXT_BUTTON_SX}>
                {groupLabel}割を印刷
              </Button>
              {canEdit && onAddStaff && (
                <Button onClick={() => setStaffDialogOpen(true)} startIcon={<Icons.PersonAdd />} sx={STANDARD_TEXT_BUTTON_SX}>
                  社員を追加
                </Button>
              )}
              {canEdit && (
                <Fab variant="extended" size="small" onClick={() => setEditing(true)} sx={STANDARD_FAB_CTA_SX}>
                  <Icons.Edit sx={{ fontSize: 18, mr: 0.5 }} />
                  配置を編集
                </Fab>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* グループカード + 未配置 (編集中はドラッグ＆ドロップ) */}
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
          {participants.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', py: 6 }}>参加者がいません</Typography>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                {groups.list.map((g) => (
                  <Card
                    key={g.key}
                    sx={{
                      borderRadius: '10px',
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: g.staffCount === 0 ? '#fca5a5' : g.staffCount >= 2 ? '#fcd34d' : '#e2e8f0',
                    }}
                  >
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Chip size="small" label={`${g.key}${groupLabel}`} sx={{ fontWeight: 800, fontSize: '12px', height: 22, bgcolor: '#1e293b', color: '#fff' }} />
                        {editing ? (
                          <TextField
                            size="small"
                            placeholder="補足 (スタート方向・時刻 など)"
                            value={g.note}
                            onChange={(e) => setGroupNote(g.key, e.target.value)}
                            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '11px', py: 0.4 } }}
                          />
                        ) : (
                          g.note && <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{g.note}</Typography>
                        )}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {g.staffCount === 0 && (
                            <Tooltip title="当社社員が配置されていません">
                              <Chip size="small" label="社員なし" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                            </Tooltip>
                          )}
                          {g.staffCount >= 2 && (
                            <Tooltip title="当社社員が複数配置されています">
                              <Chip size="small" label={`社員${g.staffCount}名`} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
                            </Tooltip>
                          )}
                          <Typography sx={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{g.members.length}名</Typography>
                        </Box>
                      </Box>
                      {g.members.map(renderMember)}
                      <DropStrip id={`end-${g.key}`} label={`この${groupLabel}の末尾へ`} disabled={!editing} />
                    </CardContent>
                  </Card>
                ))}
                {editing && (
                  <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <DropStrip id="new" label={`ここへ落とすと新しい${groupLabel}を作ります`} disabled={!editing} accent="#059669" />
                    </CardContent>
                  </Card>
                )}
              </Box>
              {(groups.unassigned.length > 0 || editing) && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b', mb: 0.5 }}>
                    未配置 ({groups.unassigned.length}名)
                  </Typography>
                  <Box sx={{ px: 1, pb: editing ? 1 : 0, border: '1px dashed #cbd5e1', borderRadius: '10px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, columnGap: 2, rowGap: 0 }}>
                      {groups.unassigned.map(renderMember)}
                    </Box>
                    <DropStrip id="unassigned" label="ここへ落とすと配置を解除します" disabled={!editing} accent="#64748b" />
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
        <DragOverlay dropAnimation={null}>
          {/* DragOverlay は幅の制約が無いと最小幅に縮んで 1 文字ずつ折り返すため、折り返し禁止と幅を明示する */}
          {active ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0.75,
                whiteSpace: 'nowrap',
                width: 'max-content',
                minWidth: 180,
                maxWidth: 360,
                px: 1.25,
                py: 0.5,
                bgcolor: '#fff',
                border: '1px solid #3b82f6',
                borderRadius: 1,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'grabbing',
              }}
            >
              <span>{fullName(active)}</span>
              <Typography component="span" sx={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>{active.company_name}</Typography>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* 社員追加ダイアログ (社員マスタから選択。追加後は未配置に入るので配置を編集で割り当てる) */}
      <Dialog open={staffDialogOpen} onClose={() => !staffSaving && setStaffDialogOpen(false)} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Icons.PersonAdd sx={{ fontSize: 18 }} />
          当社社員を参加者に追加
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>
            社員は「株式会社ホゼナル」の申込としてまとめて参加者に登録され、追加後は未配置に入ります。「配置を編集」で{groupLabel}と{positionLabel}を割り当ててください。
          </Typography>
          <Autocomplete
            multiple
            options={staffOptions}
            value={staffPick}
            onChange={(_, v) => setStaffPick(v)}
            getOptionLabel={(o: any) => `${o.staff_name}${o.branch_name ? `（${o.branch_name}）` : ''}`}
            isOptionEqualToValue={(a: any, b: any) => a.staff_id === b.staff_id}
            renderInput={(params) => <TextField {...params} size="small" label="社員" placeholder="氏名で検索" />}
            renderOption={(props, o: any) => (
              <li {...props} key={o.staff_id}>
                <Box>
                  <Typography sx={{ fontSize: '13px' }}>{o.staff_name}</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>{[o.branch_name, o.department_name].filter(Boolean).join(' / ')}</Typography>
                </Box>
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffDialogOpen(false)} disabled={staffSaving} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
          <Button onClick={handleAddStaff} disabled={staffSaving || staffPick.length === 0} startIcon={<Icons.PersonAdd />} sx={STANDARD_CTA_BUTTON_SX}>
            {staffPick.length > 0 ? `${staffPick.length} 名を追加` : '追加'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventAssignmentPanel;
